import COS from '@tencent/new-cos-js-sdk-v5';
import { getAdminDownloadCenterConfig } from '../../../../api';
import { ADMIN_PREFIX, ADMIN_PROXY_HYBRID } from '../../../../api/config';
import { getConfig } from '../../../../config';
import { wrapRequestArea } from '../../../../lib/http/lib';
import {
  getFirstManageSite,
  getNeedManageSites,
  getSiteDomain,
} from '../../../../lib/multi-site';
import { DeployArea } from '../../../../model';
import { IAPICommonCosConfigRes } from '../../../../model/api-modules';
import { getSystemTicket } from '../../../lib';
import { reportPageError } from '../../../lib/bk-report';

const cosConfig: Record<string, IAPICommonCosConfigRes | undefined> = {
  [DeployArea.cn]: undefined,
  [DeployArea.singapore]: undefined,
  [DeployArea.woa]: undefined,
};

const cosCache: Record<
  string,
  | {
      putFile: (
        fileName: string,
        file: File
      ) => Promise<COS.PutObjectResult | undefined>;
      getFileUrl: (fileName?: string | undefined) => Promise<string>;
    }
  | undefined
> = {
  [DeployArea.cn]: undefined,
  [DeployArea.singapore]: undefined,
  [DeployArea.woa]: undefined,
};

const getSiteStsPath = (propsSite?: string) => {
  const site = propsSite || getFirstManageSite();
  let path = `${ADMIN_PREFIX}/download-center/cos/sts`;

  if (getFirstManageSite() !== site) {
    const targetDomain = getSiteDomain(site);
    if (!targetDomain) {
      throw new Error('targetDomain 不存在');
    }
    path = `${ADMIN_PROXY_HYBRID}${ADMIN_PREFIX}/download-center/cos/sts?targetDomain=${targetDomain}`;
  }

  return `${location.origin}${path}`;
};

export const getLocalDownloadCosConfig = () => cosConfig;

export const getDownloadCosConfig = async (propsSite?: string) => {
  const site = propsSite || getFirstManageSite();
  if (!cosConfig[getFirstManageSite()]) {
    await Promise.all(
      getNeedManageSites().map(async (site) => {
        const siteConfigRes = await wrapRequestArea(
          getAdminDownloadCenterConfig,
          site
        );
        if (!siteConfigRes) {
          return;
        }
        const config = {
          bucket: siteConfigRes.Cos.bucket,
          region: siteConfigRes.Cos.region,
          uploadDir: siteConfigRes.UploadDir,
        };
        cosConfig[site] = config;
      })
    );

    return cosConfig[getFirstManageSite()];
  }
  return cosConfig[site];
};

export const getSiteDownloadCosInstance = (site: string) => {
  const url = getSiteStsPath(site);
  const instance = new COS({
    // getAuthorization 必选参数
    getAuthorization(options, callback) {
      // 异步获取临时密钥

      fetch(url, {
        headers: {
          ticket: getSystemTicket(),
        },
      })
        .then((response) => response.json())
        .then((data) => {
          const { credentials } = data.data;
          if (!data || !credentials) {
            return console.log('error');
          }
          callback({
            TmpSecretId: credentials.tmpSecretId,
            TmpSecretKey: credentials.tmpSecretKey,
            SecurityToken: credentials.sessionToken,
            // 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
            StartTime: data.data.startTime, // 时间戳，单位秒，如：1580000000
            ExpiredTime: data.data.expiredTime, // 时间戳，单位秒，如：1580000000
          });
        })
        .catch((error) => {
          reportPageError(error);
          console.error('Error:', error);
        });
    },
  });

  return instance;
};

export const getSiteDownloadCos = (propsSite?: string) => {
  const { deployArea } = getConfig();
  const site = propsSite || deployArea;
  const cosInstance = getSiteDownloadCosInstance(site);

  const cosObject = {
    putFile: async (fileName: string, file: File) => {
      const siteCosConfig = await getDownloadCosConfig(site);
      if (!fileName || !siteCosConfig) {
        return;
      }
      const fileKey = `${siteCosConfig.uploadDir}/${fileName}`;
      return await cosInstance.putObject({
        Bucket: siteCosConfig.bucket,
        Region: siteCosConfig.region,
        Key: fileKey,
        Body: file,
      });
    },
    getFileUrl: async (fileName?: string) => {
      const siteCosConfig = await getDownloadCosConfig(site);
      if (!fileName || !siteCosConfig) {
        return '';
      }

      const fileKey = `${siteCosConfig.uploadDir}/${fileName}`;

      const params = {
        Bucket: siteCosConfig.bucket,
        Region: siteCosConfig.region,
        Key: fileKey,
      };
      const url = await new Promise<string>((resolve, reject) => {
        cosInstance.getObjectUrl(params, (err, data) => {
          if (err) {
            reject(err);
          }
          resolve(data.Url);
        });
      });
      return url;
    },
  };

  cosCache[site] = cosObject;

  return cosObject;
};

export const initDownloadCos = () => {
  getDownloadCosConfig();
  getNeedManageSites().map((site) => {
    return getSiteDownloadCos(site);
  });
};

export const multiSiteDownloadUpload = async (fileName: string, file: File) => {
  const res = await Promise.all(
    getNeedManageSites().map((site) => {
      const cos = getSiteDownloadCos(site);
      return cos.putFile(file.name, file);
    })
  );
  if (res.every(Boolean)) {
    return res;
  }

  return [];
};

export const currentSiteDownloadUpload = async (
  fileName: string,
  file: File
) => {
  const res = await Promise.all(
    [getFirstManageSite()].map((site) => {
      const cos = getSiteDownloadCos(site);
      return cos.putFile(file.name, file);
    })
  );
  if (res.every(Boolean)) {
    return res;
  }

  return [];
};
